import appInfo from 'js/lib/appInfo';
import { getLanguageMatch, getUserLanguageCode, toLanguageCode } from 'js/lib/language';
import requestCountry from 'js/lib/requestCountry';
import { isAuthenticatedUser } from 'js/lib/user';

import type { Source } from '@coursera/event-pulse-types';

import type { Languages } from 'bundles/unified-description-page-common/providers/LanguageInfoDialogProvider';

import _t from 'i18n!nls/xdp';

const COUNTRY_LANGUAGE_MAP = {
  // French-speaking countries
  FR: ['fr'],
  BE: ['fr'], // Belgium
  CA: ['fr'], // Canada (note that Canada is bilingual but focusing on French here)
  CH: ['fr', 'de'], // Switzerland (French and German)
  LU: ['fr', 'de'], // Luxembourg (French and German)

  // German-speaking countries
  DE: ['de'],
  AT: ['de'], // Austria
  LI: ['de'], // Liechtenstein

  // Spanish-speaking countries
  ES: ['es'],
  AR: ['es'], // Argentina
  BO: ['es'], // Bolivia
  CL: ['es'], // Chile
  CO: ['es'], // Colombia
  CR: ['es'], // Costa Rica
  CU: ['es'], // Cuba
  DO: ['es'], // Dominican Republic
  EC: ['es'], // Ecuador
  SV: ['es'], // El Salvador
  GQ: ['es'], // Equatorial Guinea
  GT: ['es'], // Guatemala
  HN: ['es'], // Honduras
  MX: ['es'], // Mexico
  NI: ['es'], // Nicaragua
  PA: ['es'], // Panama
  PY: ['es'], // Paraguay
  PE: ['es'], // Peru
  PR: ['es'], // Puerto Rico
  UY: ['es'], // Uruguay
  VE: ['es'], // Venezuela

  // Brazilian Portuguese-speaking country
  BR: ['pt-br'],
};

export const getAudienceLanguageCodeFromDubbedLanguages = (dubbedLanguages: Languages['dubbedLanguages']) => {
  const languageCode = toLanguageCode(isAuthenticatedUser() ? getUserLanguageCode() : _t.getLocale());
  return dubbedLanguages?.map((lang) => lang.languageCode)?.some((lang) => getLanguageMatch(lang, languageCode))
    ? languageCode
    : null;
};

export const getAudienceLanguageFromCountryCode = (dubbedLanguages: Languages['dubbedLanguages']) => {
  const userCountryCode = requestCountry.get();
  const languages = COUNTRY_LANGUAGE_MAP[userCountryCode as keyof typeof COUNTRY_LANGUAGE_MAP];
  if (!Array.isArray(languages) || !dubbedLanguages) return null;
  const dubbedLanguageCodes = dubbedLanguages.map((lang) => lang.languageCode);

  for (const language of languages) {
    for (const dubbedLanguage of dubbedLanguageCodes) {
      if (getLanguageMatch(language, dubbedLanguage)) {
        return language;
      }
    }
  }
  return null;
};

export const getAudienceLanguageFromBrowserLanguage = (dubbedLanguages: Languages['dubbedLanguages']) => {
  const browserLanguage = appInfo.get()?.browserLanguage;
  if (!browserLanguage) return null;
  return dubbedLanguages?.map((lang) => lang.languageCode)?.some((lang) => getLanguageMatch(lang, browserLanguage))
    ? browserLanguage
    : null;
};

export const getAudienceLanguageCode = (dubbedLanguages: Languages['dubbedLanguages']) => {
  return (
    getAudienceLanguageCodeFromDubbedLanguages(dubbedLanguages) ||
    getAudienceLanguageFromCountryCode(dubbedLanguages) ||
    getAudienceLanguageFromBrowserLanguage(dubbedLanguages)
  );
};

export const getAudienceLanguageCodeSource = (dubbedLanguages: Languages['dubbedLanguages']): Source | null => {
  if (getAudienceLanguageCodeFromDubbedLanguages(dubbedLanguages)) {
    return 'profile_language';
  }
  if (getAudienceLanguageFromCountryCode(dubbedLanguages)) {
    return 'country_code';
  }
  if (getAudienceLanguageFromBrowserLanguage(dubbedLanguages)) {
    return 'browser_language';
  }

  return null;
};
